import {
  adjustHiddenProductsAfterCustomRemoval,
  adjustProductRefAfterCustomRemoval,
  productRefsEqual
} from "./chunk-N7Z63JY6.js";
import {
  removeTenantProduct,
  syncCustomProductsMirror,
  upsertUserTenantProductFromBrand
} from "./chunk-3YBVQZRI.js";
import {
  getTenantProductBySlug,
  loadTenantStore
} from "./chunk-3TOUJ5O5.js";
import {
  customProductSlugFromSuffix,
  customSuffixCollidesWithBuiltInProduct,
  productRouteSlug,
  resolveProductFromPath
} from "./chunk-OENSNHCR.js";
import {
  brandColorsEquivalent
} from "./chunk-OJN77FOL.js";
import {
  normalizeBrandAccentColor
} from "./chunk-2PN7YULH.js";
import {
  DEFAULT_CUSTOM_PRODUCT_BRAND
} from "./chunk-O4NHF6EL.js";

// src/app-store.ts
import { create } from "zustand";
import { persist } from "zustand/middleware";
var PRODUCT_SLUGS = {
  "exxat-prism": "prism",
  "exxat-design-os": "design-os",
  "exxat-admin": "admin",
  "exxat-people": "people",
  "exxat-courses": "courses",
  "exxat-personnel": "personnel",
  "exxat-one-sites": "one-sites",
  "exxat-curriculum-mapping": "curriculum-mapping",
  "exxat-compliance": "compliance",
  "exxat-surveys": "surveys",
  "exxat-exam-management": "exam-management",
  "exxat-accreditation": "accreditation",
  "exxat-student-success": "student-success",
  "exxat-custom": "custom"
};
function productSlug(product) {
  return PRODUCT_SLUGS[product];
}
function productFromPath(pathname, customProducts = []) {
  return resolveProductFromPath(pathname, customProducts)?.product ?? null;
}
function productPersistKey(product, hubKey, customProducts = [], activeCustomIndex = 0) {
  return `${productRouteSlug(product, customProducts, activeCustomIndex)}:${hubKey}`;
}
function isCustomProductPlaceholder(brand) {
  if (!brand?.suffix?.trim() || !brand?.brandColor?.trim()) return true;
  return brand.suffix.trim() === DEFAULT_CUSTOM_PRODUCT_BRAND.suffix && brandColorsEquivalent(brand.brandColor, DEFAULT_CUSTOM_PRODUCT_BRAND.brandColor);
}
function isCustomProductConfigured(brand) {
  if (!brand?.suffix?.trim() || !brand?.brandColor?.trim()) return false;
  return !isCustomProductPlaceholder(brand);
}
function isListedCustomProduct(brand) {
  return Boolean(brand?.suffix?.trim() && brand?.brandColor?.trim());
}
function hasConfiguredCustomProducts(products) {
  return (products ?? []).some(isListedCustomProduct);
}
function getActiveCustomProductBrand(state) {
  if (state.customProducts.length === 0) return null;
  const idx = Math.max(0, Math.min(state.activeCustomIndex, state.customProducts.length - 1));
  const brand = state.customProducts[idx];
  return isListedCustomProduct(brand) ? brand : null;
}
var STORE_VERSION = 14;
var LEGACY_ONE = "exxat-one";
var REMOVED_PRODUCTS = /* @__PURE__ */ new Set(["exxat-assessment"]);
var RETIRED_ONE_SCHOOLS = "exxat-one-schools";
var BUILTIN_DEFAULT_ACCENTS = {
  "exxat-prism": "oklch(60.07% 0.2312 0.68)",
  "exxat-design-os": "oklch(52% 0.14 264)",
  "exxat-admin": "oklch(45% 0.035 265)",
  "exxat-people": "oklch(48% 0.06 250)",
  "exxat-courses": "oklch(50% 0.08 230)",
  "exxat-personnel": "oklch(47% 0.05 280)",
  "exxat-one-sites": "oklch(57.84% 0.1560 279.93)",
  "exxat-curriculum-mapping": "oklch(58% 0.15 195)",
  "exxat-compliance": "oklch(56% 0.13 155)",
  "exxat-surveys": "oklch(60% 0.15 55)",
  "exxat-exam-management": "oklch(55% 0.14 300)",
  "exxat-accreditation": "oklch(54% 0.13 240)",
  "exxat-student-success": "oklch(57% 0.15 85)"
};
function migrateAppStore(persistedState, version) {
  if (!persistedState || typeof persistedState !== "object") {
    return persistedState;
  }
  const draft = { ...persistedState };
  const legacyProductId = (value) => typeof value === "string" ? value : null;
  if (version < 1) {
    if (draft.product === LEGACY_ONE) {
      draft.product = "exxat-one-schools";
    }
    if (draft.productBrandColors && typeof draft.productBrandColors === "object") {
      const colors = { ...draft.productBrandColors };
      if (LEGACY_ONE in colors) {
        colors["exxat-one-schools"] = colors[LEGACY_ONE];
        delete colors[LEGACY_ONE];
      }
      draft.productBrandColors = colors;
    }
    if (Array.isArray(draft.hiddenProductIds)) {
      draft.hiddenProductIds = draft.hiddenProductIds.map(
        (id) => id === LEGACY_ONE ? "exxat-one-schools" : id
      );
    }
  }
  if (version < 2) {
    if (typeof draft.product === "string" && REMOVED_PRODUCTS.has(draft.product)) {
      draft.product = "exxat-prism";
    }
    if (draft.productBrandColors && typeof draft.productBrandColors === "object") {
      const colors = { ...draft.productBrandColors };
      for (const removed of REMOVED_PRODUCTS) {
        delete colors[removed];
      }
      draft.productBrandColors = colors;
    }
    if (Array.isArray(draft.hiddenProductIds)) {
      draft.hiddenProductIds = draft.hiddenProductIds.filter(
        (id) => !REMOVED_PRODUCTS.has(id)
      );
    }
  }
  if (version < 3) {
    if (draft.productBrandColors && typeof draft.productBrandColors === "object") {
      const colors = { ...draft.productBrandColors };
      for (const [id, color] of Object.entries(colors)) {
        const def = BUILTIN_DEFAULT_ACCENTS[id];
        if (def && brandColorsEquivalent(color, def)) {
          delete colors[id];
        }
      }
      draft.productBrandColors = colors;
    }
  }
  if (version < 4) {
    const brand = draft.customProductBrand;
    if (brand && !isCustomProductConfigured(brand)) {
      draft.customProductBrand = null;
    }
    if (draft.product === "exxat-custom" && !isCustomProductConfigured(brand)) {
      draft.product = "exxat-prism";
    }
    if (Array.isArray(draft.hiddenProductIds)) {
      draft.hiddenProductIds = draft.hiddenProductIds.filter((id) => id !== "exxat-custom");
    }
  }
  if (version < 5) {
    const legacy = draft.customProductBrand;
    if (legacy && isCustomProductConfigured(legacy)) {
      draft.customProducts = [legacy];
      draft.activeCustomIndex = 0;
    } else {
      draft.customProducts = [];
      draft.activeCustomIndex = 0;
    }
    delete draft.customProductBrand;
    if (draft.product === "exxat-custom" && !hasConfiguredCustomProducts(draft.customProducts)) {
      draft.product = "exxat-prism";
    }
  }
  if (version < 6) {
    if (Array.isArray(draft.customProducts)) {
      draft.customProducts = draft.customProducts.map((entry) => ({
        suffix: entry.suffix,
        brandColor: normalizeBrandAccentColor(entry.brandColor)
      }));
    }
    if (draft.productBrandColors && typeof draft.productBrandColors === "object") {
      const colors = { ...draft.productBrandColors };
      for (const [id, color] of Object.entries(colors)) {
        colors[id] = normalizeBrandAccentColor(color);
      }
      draft.productBrandColors = colors;
    }
  }
  if (version < 7) {
    if (draft.productBrandColors && typeof draft.productBrandColors === "object") {
      const colors = { ...draft.productBrandColors };
      delete colors["exxat-custom"];
      draft.productBrandColors = colors;
    }
  }
  if (version < 8) {
    if (!Array.isArray(draft.hiddenProducts)) {
      const legacy = Array.isArray(draft.hiddenProductIds) ? draft.hiddenProductIds : [];
      draft.hiddenProducts = legacy.map((product) => ({ product }));
    }
    delete draft.hiddenProductIds;
    if (draft.startupProduct === void 0) {
      draft.startupProduct = null;
    }
  }
  if (version < 11) {
    const customProducts = Array.isArray(draft.customProducts) ? draft.customProducts : [];
    if (legacyProductId(draft.product) === "exxat-exam") {
      const examIndex = customProducts.findIndex(
        (cp) => cp?.suffix?.trim() && customProductSlugFromSuffix(cp.suffix) === "exam"
      );
      if (examIndex >= 0) {
        draft.product = "exxat-custom";
        draft.activeCustomIndex = examIndex;
      } else {
        draft.product = "exxat-prism";
      }
    }
    if (draft.startupProduct && typeof draft.startupProduct === "object") {
      const ref = draft.startupProduct;
      if (legacyProductId(ref.product) === "exxat-exam") {
        const examIndex = customProducts.findIndex(
          (cp) => cp?.suffix?.trim() && customProductSlugFromSuffix(cp.suffix) === "exam"
        );
        draft.startupProduct = examIndex >= 0 ? { product: "exxat-custom", customIndex: examIndex } : null;
      }
    }
    if (Array.isArray(draft.hiddenProducts)) {
      draft.hiddenProducts = draft.hiddenProducts.map(
        (ref) => legacyProductId(ref.product) === "exxat-exam" ? { product: "exxat-custom", customIndex: ref.customIndex ?? 0 } : ref
      );
    }
    if (draft.productBrandColors && typeof draft.productBrandColors === "object") {
      const colors = { ...draft.productBrandColors };
      delete colors["exxat-exam"];
      draft.productBrandColors = colors;
    }
  }
  if (version < 12) {
    const customProducts = Array.isArray(draft.customProducts) ? draft.customProducts : [];
    const examIndices = /* @__PURE__ */ new Set();
    customProducts.forEach((cp, i) => {
      if (cp?.suffix?.trim() && customProductSlugFromSuffix(cp.suffix) === "exam") {
        examIndices.add(i);
      }
    });
    if (examIndices.size > 0) {
      const nextCustom = customProducts.filter((_, i) => !examIndices.has(i));
      draft.customProducts = nextCustom;
      const adjustCustomIndex = (idx) => {
        if (idx === void 0) return void 0;
        if (examIndices.has(idx)) return void 0;
        let removedBefore = 0;
        for (const i of examIndices) {
          if (i < idx) removedBefore++;
        }
        return idx - removedBefore;
      };
      if (draft.product === "exxat-custom") {
        const activeIdx = draft.activeCustomIndex;
        if (activeIdx !== void 0 && examIndices.has(activeIdx)) {
          draft.product = "exxat-design-os";
          draft.activeCustomIndex = 0;
        } else {
          const nextIdx = adjustCustomIndex(activeIdx);
          if (nextIdx !== void 0) draft.activeCustomIndex = nextIdx;
        }
      }
      if (draft.startupProduct && typeof draft.startupProduct === "object") {
        const ref = draft.startupProduct;
        if (ref.product === "exxat-custom" && ref.customIndex !== void 0) {
          if (examIndices.has(ref.customIndex)) {
            draft.startupProduct = { product: "exxat-design-os" };
          } else {
            const nextIdx = adjustCustomIndex(ref.customIndex);
            draft.startupProduct = nextIdx !== void 0 ? { product: "exxat-custom", customIndex: nextIdx } : { product: "exxat-design-os" };
          }
        }
      }
      if (Array.isArray(draft.hiddenProducts)) {
        draft.hiddenProducts = draft.hiddenProducts.filter(
          (ref) => !(ref.product === "exxat-custom" && ref.customIndex !== void 0 && examIndices.has(ref.customIndex))
        ).map((ref) => {
          if (ref.product !== "exxat-custom" || ref.customIndex === void 0) return ref;
          const nextIdx = adjustCustomIndex(ref.customIndex);
          return nextIdx !== void 0 ? { ...ref, customIndex: nextIdx } : { product: "exxat-design-os" };
        });
      }
    }
    if (legacyProductId(draft.product) === "exxat-exam") {
      draft.product = "exxat-design-os";
    }
    if (draft.startupProduct && typeof draft.startupProduct === "object") {
      const ref = draft.startupProduct;
      if (legacyProductId(ref.product) === "exxat-exam") {
        draft.startupProduct = { product: "exxat-design-os" };
      }
    }
    if (Array.isArray(draft.hiddenProducts)) {
      draft.hiddenProducts = draft.hiddenProducts.map(
        (ref) => legacyProductId(ref.product) === "exxat-exam" ? { product: "exxat-design-os" } : ref
      );
    }
    if (draft.productBrandColors && typeof draft.productBrandColors === "object") {
      const colors = { ...draft.productBrandColors };
      delete colors["exxat-exam"];
      draft.productBrandColors = colors;
    }
  }
  if (version < 13) {
    draft.customProducts = [];
    draft.activeCustomIndex = 0;
    if (legacyProductId(draft.product) === "exxat-custom") {
      draft.product = "exxat-prism";
    }
    if (draft.startupProduct && typeof draft.startupProduct === "object") {
      const ref = draft.startupProduct;
      if (ref.product === "exxat-custom") {
        draft.startupProduct = null;
      }
    }
    if (Array.isArray(draft.hiddenProducts)) {
      draft.hiddenProducts = draft.hiddenProducts.filter(
        (ref) => ref.product !== "exxat-custom"
      );
    }
  }
  if (version < 9) {
    if (draft.productBrandColors && typeof draft.productBrandColors === "object") {
      const colors = { ...draft.productBrandColors };
      for (const [productId, color] of Object.entries(colors)) {
        if (!color || !BUILTIN_DEFAULT_ACCENTS[productId]) continue;
        let crossMatch = false;
        for (const [otherId, otherDefault] of Object.entries(BUILTIN_DEFAULT_ACCENTS)) {
          if (otherId === productId) continue;
          if (brandColorsEquivalent(color, otherDefault)) {
            crossMatch = true;
            break;
          }
        }
        if (crossMatch) {
          delete colors[productId];
        }
      }
      draft.productBrandColors = colors;
    }
  }
  if (version < 14) {
    if (legacyProductId(draft.product) === RETIRED_ONE_SCHOOLS) {
      draft.product = "exxat-one-sites";
    }
    if (draft.productBrandColors && typeof draft.productBrandColors === "object") {
      const colors = { ...draft.productBrandColors };
      delete colors[RETIRED_ONE_SCHOOLS];
      draft.productBrandColors = colors;
    }
    if (draft.startupProduct && typeof draft.startupProduct === "object") {
      const ref = draft.startupProduct;
      if (legacyProductId(ref.product) === RETIRED_ONE_SCHOOLS) {
        draft.startupProduct = null;
      }
    }
    if (Array.isArray(draft.hiddenProducts)) {
      draft.hiddenProducts = draft.hiddenProducts.filter(
        (ref) => legacyProductId(ref.product) !== RETIRED_ONE_SCHOOLS
      );
    }
    if (Array.isArray(draft.hiddenProductIds)) {
      draft.hiddenProductIds = draft.hiddenProductIds.filter(
        (id) => id !== RETIRED_ONE_SCHOOLS
      );
    }
  }
  return draft;
}
var useAppStore = create()(
  persist(
    (set) => ({
      product: "exxat-prism",
      setProduct: (product) => set({ product }),
      customProducts: [],
      activeCustomIndex: 0,
      addCustomProduct: (brand) => {
        const normalized = {
          suffix: brand.suffix.trim(),
          brandColor: normalizeBrandAccentColor(brand.brandColor)
        };
        if (customSuffixCollidesWithBuiltInProduct(normalized.suffix)) {
          let nextIndex2 = 0;
          set((state) => {
            nextIndex2 = state.activeCustomIndex;
            return state;
          });
          return nextIndex2;
        }
        let nextIndex = 0;
        if (typeof window !== "undefined") {
          const { index } = upsertUserTenantProductFromBrand(normalized);
          const mirror = syncCustomProductsMirror();
          nextIndex = index;
          set((state) => {
            const productBrandColors = { ...state.productBrandColors };
            delete productBrandColors["exxat-custom"];
            const customProducts = mirror.length > 0 ? mirror : [...state.customProducts, normalized];
            const activeCustomIndex = mirror.length > 0 ? index : customProducts.length - 1;
            return {
              customProducts,
              activeCustomIndex,
              productBrandColors
            };
          });
          return nextIndex;
        }
        set((state) => {
          const customProducts = [...state.customProducts, normalized];
          nextIndex = customProducts.length - 1;
          const productBrandColors = { ...state.productBrandColors };
          delete productBrandColors["exxat-custom"];
          return { customProducts, activeCustomIndex: nextIndex, productBrandColors };
        });
        return nextIndex;
      },
      updateCustomProduct: (index, brand) => {
        if (typeof window !== "undefined" && index >= 0) {
          upsertUserTenantProductFromBrand({
            suffix: brand.suffix.trim(),
            brandColor: normalizeBrandAccentColor(brand.brandColor)
          });
          const mirror = syncCustomProductsMirror();
          set((state) => {
            if (index >= state.customProducts.length) return state;
            const productBrandColors = { ...state.productBrandColors };
            delete productBrandColors["exxat-custom"];
            return { customProducts: mirror, productBrandColors };
          });
          return;
        }
        set((state) => {
          if (index < 0 || index >= state.customProducts.length) return state;
          const customProducts = [...state.customProducts];
          customProducts[index] = {
            suffix: brand.suffix.trim(),
            brandColor: normalizeBrandAccentColor(brand.brandColor)
          };
          const productBrandColors = { ...state.productBrandColors };
          delete productBrandColors["exxat-custom"];
          return { customProducts, productBrandColors };
        });
      },
      removeCustomProduct: (index) => set((state) => {
        if (index < 0 || index >= state.customProducts.length) return state;
        let customProducts = state.customProducts.filter((_, i) => i !== index);
        if (typeof window !== "undefined") {
          const brand = state.customProducts[index];
          if (brand?.suffix?.trim()) {
            const tenantStore = loadTenantStore();
            const record = getTenantProductBySlug(
              tenantStore,
              customProductSlugFromSuffix(brand.suffix)
            );
            if (record) {
              customProducts = removeTenantProduct(record.id);
            }
          }
        }
        let activeCustomIndex = state.activeCustomIndex;
        if (customProducts.length === 0) {
          activeCustomIndex = 0;
        } else if (activeCustomIndex >= customProducts.length) {
          activeCustomIndex = customProducts.length - 1;
        } else if (index < activeCustomIndex) {
          activeCustomIndex -= 1;
        }
        return {
          customProducts,
          activeCustomIndex,
          startupProduct: adjustProductRefAfterCustomRemoval(state.startupProduct, index),
          hiddenProducts: adjustHiddenProductsAfterCustomRemoval(
            state.hiddenProducts,
            index
          )
        };
      }),
      setActiveCustomIndex: (activeCustomIndex) => set({ activeCustomIndex }),
      setCustomProducts: (customProducts) => set({ customProducts }),
      productBrandColors: {},
      setProductBrandColor: (product, color) => set((state) => {
        if (product === "exxat-custom") return state;
        const next = { ...state.productBrandColors };
        if (color && color.trim()) {
          next[product] = normalizeBrandAccentColor(color);
        } else {
          delete next[product];
        }
        return { productBrandColors: next };
      }),
      productSwitching: false,
      setProductSwitching: (productSwitching) => set({ productSwitching }),
      hiddenProducts: [],
      hideProduct: (ref) => set((state) => ({
        hiddenProducts: state.hiddenProducts.some((entry) => productRefsEqual(entry, ref)) ? state.hiddenProducts : [...state.hiddenProducts, ref]
      })),
      showProduct: (ref) => set((state) => ({
        hiddenProducts: state.hiddenProducts.filter((entry) => !productRefsEqual(entry, ref))
      })),
      startupProduct: null,
      setStartupProduct: (startupProduct) => set({ startupProduct })
    }),
    {
      name: "exxat-app",
      skipHydration: true,
      version: STORE_VERSION,
      migrate: migrateAppStore
    }
  )
);

export {
  productSlug,
  productFromPath,
  productPersistKey,
  isCustomProductPlaceholder,
  isCustomProductConfigured,
  isListedCustomProduct,
  hasConfiguredCustomProducts,
  getActiveCustomProductBrand,
  useAppStore
};
